import cx from 'classnames'
import { Formik, useFormikContext } from 'formik'
import { BrandAwarenessPlanTerminalProps } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/Types'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import Styles from './BrandPlanAwareness.module.scss'
import { Button } from 'blaze-ui/Button'
import { ArrowLeft, ArrowRight, Mailchimp, TwitterBrand } from 'blaze-ui/icons'
import { BrandPlan, BrandPlanTactic, CrosspostFrequency } from 'types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'components/FirstTimeExperienceModal/components/Form/Form'
import { ContentsCount } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/ContentsCount'
import { FilteredContentsCount } from 'screens/BrandPlanSchedule/FilteredContentsCount'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { Heading } from 'blaze-ui/Heading/Heading'
import { FacebookBrand, WordPressBrand, GoHighLevelBrand, ShopifyBrand } from 'blaze-ui/icons/35'
import { LinkedInBrand } from 'blaze-ui/icons/32'
import { InstagramBrand } from 'blaze-ui/icons/24'
import { ChevronDown, ChevronUp, TikTokBrand, YouTubeBrand, InformationCircleContained } from 'blaze-ui/icons/20'
import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { generatePath, useHistory } from 'react-router-dom'
import { SCHEDULED_POSTS_PATH } from 'paths'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { useOnboard } from 'hooks/queries/useOnboard'
import dayjs from 'dayjs'
import { Topics } from 'components/FirstTimeExperienceModal/steps/BrandPlanAwareness/Topics'
import { SOCIAL_MEDIA_TACTICS } from 'components/FirstTimeExperienceModal/steps/BrandPlanTactics/BrandPlanTactics'
import { useStartBrandPlan } from 'hooks/queries/brandPlans/useStartBrandPlan'
import { useOnboardingTracking } from 'hooks/useOnboardingTracking'
import { useExternalIntegrations } from 'components/ExportModal/ExternalIntegrations/ExternalIntegrations'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { sendMessageToNativeApp } from 'utils/sendMessageToNativeApp'
import { useExperiment } from 'utils/experiments'
import { Text } from 'blaze-ui/Text/Text'
import { CrosspostFrequencyRadioGroup } from './CrossPostFrequencyRadioGroup'
import CrossPostIcon from './CrossPostIcon'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { useCompleteVMOnboarding } from 'hooks/queries/workspaces/useCompleteVMOnboarding'
import { InstagramReelsBrand } from 'blaze-ui/icons/30'
import { reportError } from 'utils/error'
import { useIsOnboardingRedesign } from 'hooks/useIsOnboardingRedesign'
import { useCreateOnboardingCampaign } from 'hooks/queries/adCampaigns/useCreateOnboardingCampaign'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

type ContentsItem = { type: string; count: number }

const clone = <T,>(v: T): T => JSON.parse(JSON.stringify(v))
const sumCounts = (arr: ContentsItem[] = []) => arr.reduce((s, i) => s + (i?.count || 0), 0)
const makeSingle = (count: number): ContentsItem[] => [{ type: 'post', count }]
const makeInstagram = (post: number, story: number, reel: number): ContentsItem[] => [
  { type: 'post', count: post },
  { type: 'story', count: story },
  { type: 'reel', count: reel },
]
const makeYouTube = (count: number): ContentsItem[] => [{ type: 'short', count }]

const PRESETS = {
  always: {
    cross: 4,
    perPlatform: 0,
    videoCross: 3,
    youtube: { short: 0 },
    tiktok: { post: 0 },
    ig: { post: 0, story: 1, reel: 0 },
  },
  sometimes: {
    cross: 4,
    perPlatform: 4,
    videoCross: 3,
    youtube: { short: 3 },
    tiktok: { post: 3 },
    ig: { post: 1, story: 1, reel: 3 },
  },
  never: {
    cross: 0,
    perPlatform: 4,
    videoCross: 0,
    youtube: { short: 3 },
    tiktok: { post: 3 },
    ig: { post: 4, story: 1, reel: 3 },
  },
} as const

const has = (tactics: BrandPlanTactic[], t: BrandPlanTactic) => tactics.includes(t)

const safeSet = (
  setFieldValue: (f: string, v: any, validate?: boolean) => void,
  values: any,
  field: string,
  next: any,
) => {
  const cur = values?.[field]
  if (JSON.stringify(cur) !== JSON.stringify(next)) setFieldValue(field, next, false)
}

const buildPresetPayload = (tactics: BrandPlanTactic[], freq: CrosspostFrequency) => {
  const p = PRESETS[freq]
  const snap: Record<string, ContentsItem[] | undefined> = {
    crosspost_contents: makeSingle(p.cross),
    video_crosspost_contents: makeSingle(p.videoCross),
    facebook_contents: has(tactics, BrandPlanTactic.Facebook) ? makeSingle(p.perPlatform) : undefined,
    twitter_contents: has(tactics, BrandPlanTactic.Twitter) ? makeSingle(p.perPlatform) : undefined,
    linkedin_contents: has(tactics, BrandPlanTactic.LinkedIn) ? makeSingle(p.perPlatform) : undefined,
    tiktok_contents: has(tactics, BrandPlanTactic.TikTok) ? makeSingle(p.tiktok.post) : undefined,
    youtube_contents: has(tactics, BrandPlanTactic.YouTube) ? makeYouTube(p.youtube.short) : undefined,
    instagram_contents: has(tactics, BrandPlanTactic.Instagram)
      ? makeInstagram(p.ig.post, p.ig.story, 0) // Remove reels from Instagram when InstagramReel is separate
      : undefined,
    instagram_reels_contents: has(tactics, BrandPlanTactic.InstagramReel)
      ? [{ type: 'reel', count: p.ig.reel }]
      : undefined,
  }
  return snap
}

const ensureArray = (v?: ContentsItem[]) => (Array.isArray(v) ? v : [])

const BrandPlanAwarenessTerminal = ({
  brandPlan,
  onBack,
  selectedBlogPlatform,
  selectedEmailPlatform,
  isStarterPlan = false,
}: Omit<BrandAwarenessPlanTerminalProps, 'onNext'>) => {
  const isOnboardingRedesign = useIsOnboardingRedesign()
  const { isMobile } = useResponsiveDesign()
  const { submitForm, values, setFieldValue, isSubmitting } = useFormikContext<BrandPlan>()

  const {
    seo_contents = [],
    email_contents = [],
    instagram_contents = [],
    instagram_reels_contents = [],
    facebook_contents = [],
    twitter_contents = [],
    linkedin_contents = [],
    tiktok_contents = [],
    youtube_contents = [],
    crosspost_contents = [],
    video_crosspost_contents = [],
    crosspost_frequency,
  } = values as any

  const { tactics } = brandPlan

  const [showingCrosspostContents, setShowingCrosspostContents] = useState(!isMobile)
  const [showingVideoCrosspostContents, setShowingVideoCrosspostContents] = useState(!isMobile)
  const [showingIndividualPostsContents, setShowingIndividualPostsContents] = useState(!isMobile)
  const [showingIndividualVideoPostsContents, setShowingIndividualVideoPostsContents] = useState(!isMobile)

  const { data: integrations } = useExternalIntegrations()
  const wordPressIntegration = integrations?.external_integrations?.find(
    (i) => i.provider === ExternalIntegrationEnum.WordPress,
  )
  const goHighLevelIntegration = integrations?.external_integrations?.find(
    (i) => i.provider === ExternalIntegrationEnum.GoHighLevel,
  )
  const shopifyIntegration = integrations?.external_integrations?.find(
    (i) => i.provider === ExternalIntegrationEnum.Shopify,
  )

  const shouldShowGoHighLevel =
    selectedBlogPlatform === ExternalIntegrationEnum.GoHighLevel ||
    (!selectedBlogPlatform && (goHighLevelIntegration || (!wordPressIntegration && !shopifyIntegration)))

  const getBlogIcon = () => {
    if (selectedBlogPlatform === 'generic') {
      return () => <ChannelIcon size={20} channel={ChannelType.BlogPost} />
    }

    if (shouldShowGoHighLevel) {
      return GoHighLevelBrand
    }

    if (selectedBlogPlatform === ExternalIntegrationEnum.Shopify) {
      return ShopifyBrand
    }

    return WordPressBrand
  }

  const getEmailIcon = () => {
    if (selectedEmailPlatform === 'generic') return () => <ChannelIcon size={20} channel={ChannelType.Newsletter} />
    return Mailchimp
  }

  const hasSocialMediaTactics = useMemo(() => SOCIAL_MEDIA_TACTICS.some((t) => tactics.includes(t)), [tactics])

  const savedByFrequencyRef = useRef<{
    always?: Record<string, ContentsItem[]>
    sometimes?: Record<string, ContentsItem[]>
    never?: Record<string, ContentsItem[]>
  }>({})

  useEffect(() => {
    const freq = (crosspost_frequency || 'always') as CrosspostFrequency
    if (!savedByFrequencyRef.current[freq]) {
      savedByFrequencyRef.current[freq] = {
        crosspost_contents: clone(crosspost_contents),
        video_crosspost_contents: clone(video_crosspost_contents),
        facebook_contents: clone(facebook_contents),
        twitter_contents: clone(twitter_contents),
        linkedin_contents: clone(linkedin_contents),
        tiktok_contents: clone(tiktok_contents),
        youtube_contents: clone(youtube_contents),
        instagram_contents: clone(instagram_contents),
        instagram_reels_contents: clone(instagram_reels_contents),
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setIfChanged = useMemo(
    () => (field: string, nextVal: ContentsItem[]) => safeSet(setFieldValue, values, field, nextVal),
    [values, setFieldValue],
  )

  const handleCrosspostFrequencyChange = useCallback(
    (next: CrosspostFrequency) => {
      const currentFreq = ((values as any)['crosspost_frequency'] as CrosspostFrequency) || 'always'
      savedByFrequencyRef.current[currentFreq] = {
        crosspost_contents: clone((values as any)['crosspost_contents'] || []),
        video_crosspost_contents: clone((values as any)['video_crosspost_contents'] || []),
        facebook_contents: clone((values as any)['facebook_contents'] || []),
        twitter_contents: clone((values as any)['twitter_contents'] || []),
        linkedin_contents: clone((values as any)['linkedin_contents'] || []),
        tiktok_contents: clone((values as any)['tiktok_contents'] || []),
        youtube_contents: clone((values as any)['youtube_contents'] || []),
        instagram_contents: clone((values as any)['instagram_contents'] || []),
        instagram_reels_contents: clone((values as any)['instagram_reels_contents'] || []),
      }

      setFieldValue('crosspost_frequency', next, false)

      const saved = savedByFrequencyRef.current[next]
      const preset = buildPresetPayload(tactics, next)

      const nextSnap = {
        crosspost_contents: ensureArray(saved?.crosspost_contents ?? preset.crosspost_contents ?? []),
        video_crosspost_contents: ensureArray(saved?.video_crosspost_contents ?? preset.video_crosspost_contents ?? []),
        facebook_contents: ensureArray(saved?.facebook_contents ?? preset.facebook_contents ?? []),
        twitter_contents: ensureArray(saved?.twitter_contents ?? preset.twitter_contents ?? []),
        linkedin_contents: ensureArray(saved?.linkedin_contents ?? preset.linkedin_contents ?? []),
        tiktok_contents: ensureArray(saved?.tiktok_contents ?? preset.tiktok_contents ?? []),
        youtube_contents: ensureArray(saved?.youtube_contents ?? preset.youtube_contents ?? []),
        instagram_contents: ensureArray(saved?.instagram_contents ?? preset.instagram_contents ?? []),
        instagram_reels_contents: ensureArray(saved?.instagram_reels_contents ?? preset.instagram_reels_contents ?? []),
      }

      if (has(tactics, BrandPlanTactic.Facebook)) setIfChanged('facebook_contents', nextSnap.facebook_contents)
      if (has(tactics, BrandPlanTactic.Twitter)) setIfChanged('twitter_contents', nextSnap.twitter_contents)
      if (has(tactics, BrandPlanTactic.LinkedIn)) setIfChanged('linkedin_contents', nextSnap.linkedin_contents)
      if (has(tactics, BrandPlanTactic.TikTok)) setIfChanged('tiktok_contents', nextSnap.tiktok_contents)
      if (has(tactics, BrandPlanTactic.YouTube)) setIfChanged('youtube_contents', nextSnap.youtube_contents)
      if (has(tactics, BrandPlanTactic.Instagram)) {
        setIfChanged('instagram_contents', nextSnap.instagram_contents)
      }
      if (has(tactics, BrandPlanTactic.InstagramReel)) {
        setIfChanged('instagram_reels_contents', nextSnap.instagram_reels_contents)
      }
      setIfChanged('crosspost_contents', nextSnap.crosspost_contents)
      setIfChanged('video_crosspost_contents', nextSnap.video_crosspost_contents)
    },
    [values, setFieldValue, setIfChanged, tactics],
  )

  const totalSocialMediaPosts = useMemo(() => {
    let total = 0
    if (has(tactics, BrandPlanTactic.Instagram)) total += sumCounts(instagram_contents)
    if (has(tactics, BrandPlanTactic.Facebook)) total += sumCounts(facebook_contents)
    if (has(tactics, BrandPlanTactic.Twitter)) total += sumCounts(twitter_contents)
    if (has(tactics, BrandPlanTactic.LinkedIn)) total += sumCounts(linkedin_contents)
    // Note: Instagram Reels are NOT included in social media total - they belong to Short Form Video
    return total
  }, [tactics, instagram_contents, facebook_contents, twitter_contents, linkedin_contents])

  const hasSocialTopics = useMemo(() => {
    if (has(tactics, BrandPlanTactic.Instagram)) return true
    if (has(tactics, BrandPlanTactic.Facebook)) return true
    if (has(tactics, BrandPlanTactic.Twitter)) return true
    if (has(tactics, BrandPlanTactic.LinkedIn)) return true
    return false
  }, [tactics])

  const minSocialTopics = useMemo(() => {
    const counts: number[] = []
    if (has(tactics, BrandPlanTactic.Instagram)) counts.push(sumCounts(instagram_contents))
    if (has(tactics, BrandPlanTactic.Facebook)) counts.push(sumCounts(facebook_contents))
    if (has(tactics, BrandPlanTactic.Twitter)) counts.push(sumCounts(twitter_contents))
    if (has(tactics, BrandPlanTactic.LinkedIn)) counts.push(sumCounts(linkedin_contents))
    // Note: Instagram Reels are NOT included in social topics - they belong to Short Form Video
    return counts.length ? Math.max(...counts) : 0
  }, [tactics, instagram_contents, facebook_contents, twitter_contents, linkedin_contents])

  const crosspostTotal = useMemo(() => sumCounts(crosspost_contents), [crosspost_contents])
  const videoCrosspostTotal = useMemo(() => sumCounts(video_crosspost_contents), [video_crosspost_contents])

  const hasVideoTactics = useMemo(
    () =>
      has(tactics, BrandPlanTactic.InstagramReel) ||
      has(tactics, BrandPlanTactic.YouTube) ||
      has(tactics, BrandPlanTactic.TikTok),
    [tactics],
  )

  const showOnlyCrosspost = (crosspost_frequency || 'always') === 'always' && hasSocialMediaTactics
  const submitLabel = isOnboardingRedesign ? 'Generate my First Week of Content' : 'Generate Content'

  return (
    <Form className={Styles.form}>
      <StickyFooterProvider
        footer={
          <StickyFooter>
            <FooterButtons>
              <ResponsiveDesign.Desktop>
                <Button
                  id="brand-plan-awareness-back-terminal"
                  variant="tertiary"
                  size={isOnboardingRedesign ? 'lg' : 'xl'}
                  onPress={onBack}
                  frontIcon={isOnboardingRedesign ? undefined : ArrowLeft}
                  aria-label="Back"
                >
                  Back
                </Button>
              </ResponsiveDesign.Desktop>
              <Button
                id="brand-plan-awareness-next-terminal"
                size={isOnboardingRedesign ? 'lg' : 'xl'}
                onPress={() => submitForm()}
                endIcon={isOnboardingRedesign ? undefined : ArrowRight}
                aria-label="Generate Content"
                isDisabled={isSubmitting}
                fullWidth={isMobile}
              >
                {isSubmitting ? 'Generating...' : submitLabel}
              </Button>
            </FooterButtons>
          </StickyFooter>
        }
      >
        {!isStarterPlan && !isOnboardingRedesign && (hasSocialMediaTactics || hasVideoTactics) && (
          <div className={cx(Styles.section, Styles.crossPostFrequencySection)}>
            <div className={Styles.headingAndDescription}>
              <Heading level={4}>Cross posting shares the same post across multiple platforms.</Heading>
              <Heading level={4}>How often would you like us to cross post your content?</Heading>
            </div>
            <div className={Styles.crosspostFrequency}>
              <CrosspostFrequencyRadioGroup
                value={(crosspost_frequency || 'always') as CrosspostFrequency}
                onChange={handleCrosspostFrequencyChange}
                chipSize="lg"
                chipClassName={Styles.crosspostFrequencyChip}
              />
            </div>
          </div>
        )}

        {hasSocialMediaTactics && (
          <div className={cx(Styles.section, Styles.socialMediaSection)}>
            <div className={Styles.socialMediaContent}>
              <div className={Styles.leftContent}>
                <Heading level={isOnboardingRedesign ? 3 : 4}>Social Media</Heading>

                {(crosspost_frequency || 'always') !== 'never' && (
                  <div className={Styles.crosspostSection}>
                    <div className={Styles.socialMediaHeader}>
                      <div className={Styles.socialMediaHeaderText}>
                        <Text variant="label" color="var(--dark-60)">
                          Cross posts
                        </Text>
                        {crosspost_frequency !== 'never' && (
                          <Tooltip label="Cross posts are posted to multiple platforms at once.">
                            <div>
                              <InformationCircleContained size={15} color="var(--dark-60)" />
                            </div>
                          </Tooltip>
                        )}
                      </div>
                      <ResponsiveDesign.Mobile>
                        {showingCrosspostContents && (
                          <Button
                            variant="editor"
                            endIcon={ChevronUp}
                            size="sm"
                            onPress={() => setShowingCrosspostContents(false)}
                          >
                            Show Less
                          </Button>
                        )}
                      </ResponsiveDesign.Mobile>
                    </div>
                    <div className={Styles.contents}>
                      <ResponsiveDesign.Mobile>
                        {!showingCrosspostContents && (
                          <div className={Styles.collapsedTactics}>
                            <div className={Styles.icons}>
                              {has(brandPlan.tactics, BrandPlanTactic.Instagram) && (
                                <div className={cx(Styles.icon, Styles[BrandPlanTactic.Instagram])}>
                                  <InstagramBrand size={33} />
                                </div>
                              )}
                              {has(brandPlan.tactics, BrandPlanTactic.Facebook) && (
                                <div className={cx(Styles.icon, Styles[BrandPlanTactic.Facebook])}>
                                  <FacebookBrand size={35} />
                                </div>
                              )}
                              {has(brandPlan.tactics, BrandPlanTactic.Twitter) && (
                                <div className={cx(Styles.icon, Styles[BrandPlanTactic.Twitter])}>
                                  <TwitterBrand size={33} />
                                </div>
                              )}
                              {has(brandPlan.tactics, BrandPlanTactic.LinkedIn) && (
                                <div className={cx(Styles.icon, Styles[BrandPlanTactic.LinkedIn])}>
                                  <LinkedInBrand size={33} />
                                </div>
                              )}
                            </div>
                            <Button
                              variant="editor"
                              endIcon={ChevronDown}
                              size="sm"
                              onPress={() => setShowingCrosspostContents(true)}
                            >
                              {crosspostTotal} posts per week
                            </Button>
                          </div>
                        )}
                      </ResponsiveDesign.Mobile>
                      {showingCrosspostContents && (
                        <ContentsCount
                          name="crosspost_contents"
                          weekLabel="wk"
                          icon={
                            <CrossPostIcon
                              providers={
                                [
                                  has(brandPlan.tactics, BrandPlanTactic.Instagram) &&
                                    ExternalIntegrationEnum.Instagram,
                                  has(brandPlan.tactics, BrandPlanTactic.Facebook) && ExternalIntegrationEnum.Facebook,
                                  has(brandPlan.tactics, BrandPlanTactic.Twitter) && ExternalIntegrationEnum.X,
                                  has(brandPlan.tactics, BrandPlanTactic.LinkedIn) && ExternalIntegrationEnum.LinkedIn,
                                ].filter(Boolean) as ExternalIntegrationEnum[]
                              }
                            />
                          }
                        />
                      )}
                    </div>
                  </div>
                )}

                {hasSocialTopics && !showOnlyCrosspost && (
                  <div className={Styles.socialMediaSection}>
                    {!isStarterPlan && (
                      <div className={Styles.socialMediaHeader}>
                        <div className={Styles.socialMediaHeaderText}>
                          <Text variant="label" color="var(--dark-60)">
                            Individual posts
                          </Text>
                          <Tooltip label="Individual posts are only posted to a specific platform.">
                            <div>
                              <InformationCircleContained size={15} color="var(--dark-60)" />
                            </div>
                          </Tooltip>
                        </div>
                        <ResponsiveDesign.Mobile>
                          {showingIndividualPostsContents && (
                            <Button
                              variant="editor"
                              endIcon={ChevronUp}
                              size="sm"
                              onPress={() => setShowingIndividualPostsContents(false)}
                            >
                              Show Less
                            </Button>
                          )}
                        </ResponsiveDesign.Mobile>
                      </div>
                    )}
                    <div className={Styles.contents}>
                      <ResponsiveDesign.Mobile>
                        {!showingIndividualPostsContents && (
                          <div className={Styles.collapsedTactics}>
                            <div className={Styles.icons}>
                              {has(tactics, BrandPlanTactic.Instagram) && !showOnlyCrosspost && (
                                <div className={cx(Styles.icon, Styles[BrandPlanTactic.Instagram])}>
                                  <InstagramBrand size={33} />
                                </div>
                              )}
                              {!showOnlyCrosspost && has(tactics, BrandPlanTactic.Facebook) && (
                                <div className={cx(Styles.icon, Styles[BrandPlanTactic.Facebook])}>
                                  <FacebookBrand size={35} />
                                </div>
                              )}
                              {!showOnlyCrosspost && has(tactics, BrandPlanTactic.LinkedIn) && (
                                <div className={cx(Styles.icon, Styles[BrandPlanTactic.LinkedIn])}>
                                  <LinkedInBrand size={33} />
                                </div>
                              )}
                              {!showOnlyCrosspost && has(tactics, BrandPlanTactic.Twitter) && (
                                <div className={cx(Styles.icon, Styles[BrandPlanTactic.Twitter])}>
                                  <TwitterBrand size={33} />
                                </div>
                              )}
                            </div>
                            <Button
                              variant="editor"
                              endIcon={ChevronDown}
                              size="sm"
                              onPress={() => setShowingIndividualPostsContents(true)}
                            >
                              {totalSocialMediaPosts} posts per week
                            </Button>
                          </div>
                        )}
                      </ResponsiveDesign.Mobile>
                      {showingIndividualPostsContents &&
                        has(tactics, BrandPlanTactic.Instagram) &&
                        !showOnlyCrosspost && (
                          <FilteredContentsCount
                            name="instagram_contents"
                            icon={InstagramBrand}
                            excludeTypes={['reel']}
                          />
                        )}
                      {showingIndividualPostsContents &&
                        !showOnlyCrosspost &&
                        has(tactics, BrandPlanTactic.Facebook) && (
                          <ContentsCount name="facebook_contents" icon={FacebookBrand} />
                        )}
                      {showingIndividualPostsContents &&
                        !showOnlyCrosspost &&
                        has(tactics, BrandPlanTactic.Twitter) && (
                          <ContentsCount name="twitter_contents" icon={TwitterBrand} />
                        )}
                      {showingIndividualPostsContents &&
                        !showOnlyCrosspost &&
                        has(tactics, BrandPlanTactic.LinkedIn) && (
                          <ContentsCount name="linkedin_contents" icon={LinkedInBrand} />
                        )}
                    </div>
                  </div>
                )}
              </div>
              <div className={Styles.rightContent}>
                <Topics name="social_topics" minValue={Math.max(minSocialTopics, crosspostTotal)} />
              </div>
            </div>
          </div>
        )}

        {hasVideoTactics && (
          <div className={cx(Styles.section, Styles.socialMediaSection)}>
            <div className={Styles.socialMediaContent}>
              <div className={Styles.leftContent}>
                <Heading level={isOnboardingRedesign ? 3 : 4}>Short Form Video</Heading>
                {(crosspost_frequency || 'always') !== 'never' && (
                  <div className={Styles.crosspostSection}>
                    <div className={Styles.socialMediaHeader}>
                      <div className={Styles.socialMediaHeaderText}>
                        <Text variant="label" color="var(--dark-60)">
                          Video cross posts
                        </Text>
                        <Tooltip label="Video cross posts are posted to multiple video platforms at once.">
                          <div>
                            <InformationCircleContained size={15} color="var(--dark-60)" />
                          </div>
                        </Tooltip>
                      </div>
                      <ResponsiveDesign.Mobile>
                        {showingVideoCrosspostContents && (
                          <Button
                            variant="editor"
                            endIcon={ChevronUp}
                            size="sm"
                            onPress={() => setShowingVideoCrosspostContents(false)}
                          >
                            Show Less
                          </Button>
                        )}
                      </ResponsiveDesign.Mobile>
                    </div>
                    <div className={Styles.contents}>
                      <ResponsiveDesign.Mobile>
                        {!showingVideoCrosspostContents && (
                          <div className={Styles.collapsedTactics}>
                            <div className={Styles.icons}>
                              {has(brandPlan.tactics, BrandPlanTactic.InstagramReel) && (
                                <div className={cx(Styles.icon, Styles[BrandPlanTactic.InstagramReel])}>
                                  <InstagramReelsBrand size={33} />
                                </div>
                              )}
                              {has(brandPlan.tactics, BrandPlanTactic.YouTube) && (
                                <div className={cx(Styles.icon, Styles[BrandPlanTactic.YouTube])}>
                                  <YouTubeBrand size={33} />
                                </div>
                              )}
                              {has(brandPlan.tactics, BrandPlanTactic.TikTok) && (
                                <div className={cx(Styles.icon, Styles[BrandPlanTactic.TikTok])}>
                                  <TikTokBrand size={33} />
                                </div>
                              )}
                            </div>
                            <Button
                              variant="editor"
                              endIcon={ChevronDown}
                              size="sm"
                              onPress={() => setShowingVideoCrosspostContents(true)}
                            >
                              {videoCrosspostTotal} posts per week
                            </Button>
                          </div>
                        )}
                      </ResponsiveDesign.Mobile>
                      {showingVideoCrosspostContents && (
                        <ContentsCount
                          name="video_crosspost_contents"
                          weekLabel="wk"
                          icon={
                            <CrossPostIcon
                              isReels
                              providers={
                                [
                                  has(brandPlan.tactics, BrandPlanTactic.InstagramReel) &&
                                    ExternalIntegrationEnum.Instagram,
                                  has(brandPlan.tactics, BrandPlanTactic.YouTube) && ExternalIntegrationEnum.YouTube,
                                  has(brandPlan.tactics, BrandPlanTactic.TikTok) && ExternalIntegrationEnum.TikTok,
                                ].filter(Boolean) as ExternalIntegrationEnum[]
                              }
                            />
                          }
                        />
                      )}
                    </div>
                  </div>
                )}

                {(crosspost_frequency || 'always') !== 'always' && (
                  <div className={Styles.socialMediaSection}>
                    {!isStarterPlan && (
                      <div className={Styles.socialMediaHeader}>
                        <div className={Styles.socialMediaHeaderText}>
                          <Text variant="label" color="var(--dark-60)">
                            Individual posts
                          </Text>
                          <Tooltip label="Individual posts are only posted to a specific platform.">
                            <div>
                              <InformationCircleContained size={15} color="var(--dark-60)" />
                            </div>
                          </Tooltip>
                        </div>
                        <ResponsiveDesign.Mobile>
                          {showingIndividualVideoPostsContents && (
                            <Button
                              variant="editor"
                              endIcon={ChevronUp}
                              size="sm"
                              onPress={() => setShowingIndividualVideoPostsContents(false)}
                            >
                              Show Less
                            </Button>
                          )}
                        </ResponsiveDesign.Mobile>
                      </div>
                    )}
                    <div className={Styles.contents}>
                      <ResponsiveDesign.Mobile>
                        {!showingIndividualVideoPostsContents && (
                          <div className={Styles.collapsedTactics}>
                            <div className={Styles.icons}>
                              {has(tactics, BrandPlanTactic.InstagramReel) && (
                                <div className={cx(Styles.icon, Styles[BrandPlanTactic.InstagramReel])}>
                                  <InstagramReelsBrand size={33} />
                                </div>
                              )}
                              {has(tactics, BrandPlanTactic.TikTok) && (
                                <div className={cx(Styles.icon, Styles[BrandPlanTactic.TikTok])}>
                                  <TikTokBrand size={33} />
                                </div>
                              )}
                              {has(tactics, BrandPlanTactic.YouTube) && (
                                <div className={cx(Styles.icon, Styles[BrandPlanTactic.YouTube])}>
                                  <YouTubeBrand size={33} />
                                </div>
                              )}
                            </div>
                            <Button
                              variant="editor"
                              endIcon={ChevronDown}
                              size="sm"
                              onPress={() => setShowingIndividualVideoPostsContents(true)}
                            >
                              {sumCounts(instagram_reels_contents) +
                                sumCounts(tiktok_contents) +
                                sumCounts(youtube_contents)}{' '}
                              posts per week
                            </Button>
                          </div>
                        )}
                      </ResponsiveDesign.Mobile>
                      {showingIndividualVideoPostsContents && has(tactics, BrandPlanTactic.InstagramReel) && (
                        <ContentsCount name="instagram_reels_contents" icon={InstagramReelsBrand} />
                      )}
                      {showingIndividualVideoPostsContents && has(tactics, BrandPlanTactic.TikTok) && (
                        <ContentsCount name="tiktok_contents" icon={TikTokBrand} />
                      )}
                      {showingIndividualVideoPostsContents && has(tactics, BrandPlanTactic.YouTube) && (
                        <ContentsCount name="youtube_contents" icon={YouTubeBrand} />
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className={Styles.rightContent}>
                <Topics
                  name="short_form_video_topics"
                  minValue={Math.max(
                    (crosspost_frequency || 'always') !== 'never' ? videoCrosspostTotal : 0,
                    (crosspost_frequency || 'always') !== 'always'
                      ? Math.max(
                          sumCounts(instagram_reels_contents),
                          sumCounts(tiktok_contents),
                          sumCounts(youtube_contents),
                        )
                      : 0,
                  )}
                />
              </div>
            </div>
          </div>
        )}

        {has(tactics, BrandPlanTactic.SEO) && (
          <div className={Styles.section}>
            <div className={Styles.leftContent}>
              <Heading level={isOnboardingRedesign ? 3 : 4}>Blog</Heading>
              <div className={Styles.contents}>
                <ContentsCount name="seo_contents" icon={getBlogIcon()} />
              </div>
            </div>
            <div className={Styles.rightContent}>
              <Topics
                name="seo_topics"
                minValue={seo_contents.reduce((sum: number, item: ContentsItem) => sum + item.count, 0)}
              />
            </div>
          </div>
        )}

        {has(tactics, BrandPlanTactic.Email) && (
          <div className={Styles.section}>
            <div className={Styles.leftContent}>
              <Heading level={isOnboardingRedesign ? 3 : 4}>Emails</Heading>
              <div className={Styles.contents}>
                <ContentsCount name="email_contents" icon={getEmailIcon()} />
              </div>
            </div>
            <div className={Styles.rightContent}>
              <Topics
                name="email_topics"
                minValue={email_contents.reduce((sum: number, item: ContentsItem) => sum + item.count, 0)}
              />
            </div>
          </div>
        )}
      </StickyFooterProvider>
    </Form>
  )
}

const BrandAwarenessPlanTerminalFormikContainer = ({
  brandPlan,
  onNext,
  isStarterPlan = false,
  paidAdsOnboardingParameters,
  ...rest
}: BrandAwarenessPlanTerminalProps) => {
  const isAdsOnboardingEnabled = useFlipperFlag('ads_onboarding')
  const { mutateAsync: updateProfile } = useUpdateBrandPlan({ id: brandPlan.id })
  const { mutate: createOnboardingCampaign } = useCreateOnboardingCampaign({
    onError: (error) => {
      reportError(error, { severity: 'warning' })
    },
  })

  const initialValues = useMemo(() => {
    const {
      facebook_contents,
      facebook_topics,
      instagram_contents,
      instagram_reels_contents,
      instagram_topics,
      twitter_contents,
      twitter_topics,
      linkedin_topics,
      linkedin_contents,
      tiktok_contents,
      tiktok_topics,
      youtube_contents,
      youtube_topics,
      seo_contents,
      seo_topics,
      email_contents,
      email_topics,
      crosspost_topics,
      crosspost_frequency,
      crosspost_contents,
      video_crosspost_contents = [],
      video_crosspost_topics = [],
      tactics,
    } = brandPlan as any

    const freq = isStarterPlan ? 'never' : ((crosspost_frequency || 'always') as CrosspostFrequency)
    const preset = buildPresetPayload(tactics, freq)

    // Collect all social topics
    const allSocialTopics = [
      ...(facebook_topics ?? []),
      ...(instagram_topics ?? []),
      ...(linkedin_topics ?? []),
      ...(twitter_topics ?? []),
      ...(crosspost_topics ?? []),
    ]

    const allVideoTopics = [
      ...(tiktok_topics ?? []),
      ...(youtube_topics ?? []),
      ...(video_crosspost_topics ?? []),
    ]

    const getContents = (
      tactic: BrandPlanTactic | null,
      brandPlanValue: ContentsItem[] | undefined,
      presetValue: ContentsItem[] | undefined,
    ) => {
      if (tactic && !has(tactics, tactic)) {
        return ensureArray(brandPlanValue)
      }
      return ensureArray(isStarterPlan ? presetValue : brandPlanValue ?? presetValue)
    }

    return {
      social_topics: allSocialTopics,
      short_form_video_topics: allVideoTopics,
      facebook_topics: facebook_topics ?? [],
      instagram_topics: instagram_topics ?? [],
      twitter_topics: twitter_topics ?? [],
      linkedin_topics: linkedin_topics ?? [],
      tiktok_topics: tiktok_topics ?? [],
      seo_topics: seo_topics ?? [],
      email_topics: email_topics ?? [],
      crosspost_topics: [],
      video_crosspost_topics: [],
      facebook_contents: getContents(BrandPlanTactic.Facebook, facebook_contents, preset.facebook_contents),
      instagram_contents: getContents(BrandPlanTactic.Instagram, instagram_contents, preset.instagram_contents),
      instagram_reels_contents: getContents(
        BrandPlanTactic.InstagramReel,
        instagram_reels_contents,
        preset.instagram_reels_contents,
      ),
      twitter_contents: getContents(BrandPlanTactic.Twitter, twitter_contents, preset.twitter_contents),
      linkedin_contents: getContents(BrandPlanTactic.LinkedIn, linkedin_contents, preset.linkedin_contents),
      tiktok_contents: getContents(BrandPlanTactic.TikTok, tiktok_contents, preset.tiktok_contents),
      youtube_contents: getContents(BrandPlanTactic.YouTube, youtube_contents, preset.youtube_contents),
      seo_contents: ensureArray(seo_contents),
      email_contents: ensureArray(email_contents),
      crosspost_frequency: freq,
      crosspost_contents: getContents(null, isStarterPlan ? undefined : crosspost_contents, preset.crosspost_contents),
      video_crosspost_contents: getContents(
        null,
        isStarterPlan ? undefined : video_crosspost_contents,
        preset.video_crosspost_contents,
      ),
      tactics,
    }
  }, [brandPlan, isStarterPlan])

  const { isMobile, isIOSNative } = useResponsiveDesign()
  const mobileCalendarAccessVariant = useExperiment('mobile_calendar_access')
  const history = useHistory()
  const { mutateAsync: onboard } = useOnboard()
  const { mutateAsync: startBrandPlan } = useStartBrandPlan({ brandPlanId: brandPlan.id })
  const { trackGenerateContentClicked, trackTopicsReviewed } = useOnboardingTracking()
  const { mutateAsync: markVirtualMarketerOnboardingCompleted } = useCompleteVMOnboarding()

  const handleSubmit = useCallback(
    async ({
      social_topics,
      short_form_video_topics,
      instagram_reels_contents,
      ...restValues
    }: Partial<
      Omit<
        BrandPlan,
        | 'id'
        | 'facebook_topics'
        | 'instagram_topics'
        | 'twitter_topics'
        | 'linkedin_topics'
        | 'tiktok_topics'
        | 'youtube_topics'
        | 'crosspost_topics'
        | 'video_crosspost_topics'
        | 'strategy'
      >
    > & { social_topics: string[]; short_form_video_topics?: string[]; instagram_reels_contents?: any[] }) => {
      const payload: any = { ...restValues }

      // Merge short form video topics back into social topics
      const mergedTopics = [...(social_topics ?? []), ...(short_form_video_topics ?? [])]
      payload.instagram_topics = mergedTopics

      // InstagramReel is now a separate tactic, so don't merge it back into Instagram

      payload.facebook_topics = []
      payload.linkedin_topics = []
      payload.twitter_topics = []
      payload.tiktok_topics = []
      payload.youtube_topics = []
      payload.crosspost_topics = []
      payload.video_crosspost_topics = []
      // Remove the short_form_video_topics field from payload as it's not part of the API
      delete payload.short_form_video_topics
      try {
        await updateProfile(payload)
        const { trackGenerateContentClicked: trackGen, trackTopicsReviewed: trackTopics } = {
          trackGenerateContentClicked,
          trackTopicsReviewed,
        }
        trackTopics()
        trackGen()

        const shouldOnboardForAds =
          !!paidAdsOnboardingParameters &&
          !!paidAdsOnboardingParameters?.selectedPlatforms?.length &&
          paidAdsOnboardingParameters?.weeklyAdsBudget !== undefined
        if (isAdsOnboardingEnabled && shouldOnboardForAds) {
          createOnboardingCampaign({ weeklyBudget: paidAdsOnboardingParameters.weeklyAdsBudget ?? 0 })
        }

        const { brand_plan: brandPlan, first_schedule: schedule } = await startBrandPlan({})
        await onboard()
        await markVirtualMarketerOnboardingCompleted()

        const pathname = generatePath(SCHEDULED_POSTS_PATH, {
          workspaceId: brandPlan.workspace_id,
        })

        if (schedule?.id) {
          const search = `?date=${dayjs(schedule.start_date).format('YYYY-MM-DD')}&load-tutorial=true`
          if (isIOSNative) {
            sendMessageToNativeApp('didFinishOnboarding')
            return
          }
          // A/B test: block/allow mobile calendar
          if (isMobile && mobileCalendarAccessVariant !== 'allow') {
            onNext({
              desktopPath: `${pathname}${search}`,
              publishDay: dayjs(schedule.start_date).format('dddd'),
            })
          } else {
            history.replace({ pathname, search })
          }
        } else {
          showErrorMessageToast('Failed to generate content, please try again.')
        }
      } catch (e) {
        showErrorMessageToast('Failed to generate content, please try again.')
        reportError(e)
      }
    },
    [
      updateProfile,
      markVirtualMarketerOnboardingCompleted,
      startBrandPlan,
      history,
      isMobile,
      isIOSNative,
      mobileCalendarAccessVariant,
      onboard,
      onNext,
      trackGenerateContentClicked,
      trackTopicsReviewed,
      paidAdsOnboardingParameters,
      createOnboardingCampaign,
      isAdsOnboardingEnabled,
    ],
  )

  return (
    <Formik onSubmit={handleSubmit} initialValues={initialValues} enableReinitialize>
      <BrandPlanAwarenessTerminal brandPlan={brandPlan} isStarterPlan={isStarterPlan} {...rest} />
    </Formik>
  )
}

export { BrandAwarenessPlanTerminalFormikContainer as BrandAwarenessPlanTerminal }
